import type { ChangeEvent } from 'react';

import { useTranslation } from 'react-i18next';

import ToastService from '~/services/toast.service';

import { MAX_FILE_BYTES, MAX_FILE_MB } from './constants';

/**
 * Returns a curry: pass it a state setter, get back an `<input type="file">`
 * change handler that
 *
 * - validates the file size against `MAX_FILE_BYTES`,
 * - shows a toast if it's too large,
 * - resets the input value so picking the same file twice still re-fires
 *   onChange.
 */
export function useFileAttachment() {
  const { t } = useTranslation('bugReport');

  return (setter: (file: File) => void) =>
    (event: ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];

      event.target.value = '';

      if (!file) {
        return;
      }

      if (file.size > MAX_FILE_BYTES) {
        ToastService.error([
          t(($) => $.dialog.evidence.fileTooLarge, {
            fileName: file.name,
            maxMb: String(MAX_FILE_MB),
            ns: 'bugReport',
          }),
        ]);
        return;
      }

      setter(file);
    };
}
